import { useState } from 'react';
import { formatCurrency } from '@libs/utils/helpers/formatCurrency';

import { MatchMakerDecision } from '../../../../../services/matchMaker/types';
import { MMHighlightsProps } from '.';

export const useMMHighlights = ({ offer }: MMHighlightsProps) => {
  const isAnnuityFree = getAnnuityFree(offer?.matchMaker);
  const annuityText = getAnnuityText(offer?.matchMaker);

  const annuityInfo = offer?.matchMaker?.annuityDetails?.find((detail) => detail.component === 'info_feature');
  const travelInfo = offer?.matchMaker?.loyalty?.find((detail) => detail?.component === 'travel_benefits');
  const incomeInfo = offer?.matchMaker?.featureDescriptions[1];
  const pointsInfo = offer?.matchMaker?.loyalty.find((detail) => detail.component === 'points_program');
  const discountInfo = offer?.matchMaker?.discount.find((detail) => detail.component === 'info_feature');

  const highlights = {
    discount: {
      highlightTitle: 'Desconto',
      highlightDescription: discountInfo?.text_button || 'Não tem',
      hasModalInfo: !!discountInfo?.description,
      icon: discountInfo?.icon?.[0]?.filename || '',
      modalTitle: discountInfo?.title,
      modalDescription: discountInfo?.description,
    },
    income: {
      highlightTitle: incomeInfo?.title,
      highlightDescription: incomeInfo?.description,
      hasModalInfo: false,
      icon: '',
      modalTitle: null,
      modalDescription: null,
    },
    travel: {
      highlightTitle: 'Vantagens para viajar',
      highlightDescription: travelInfo?.text_button || 'Não tem',
      hasModalInfo: !!travelInfo?.description,
      icon: travelInfo?.icon?.[0].filename || '',
      modalTitle: travelInfo?.title,
      modalDescription: travelInfo?.description,
    },
    points: {
      highlightTitle: 'Programa de pontos',
      highlightDescription: pointsInfo?.name || 'Não tem',
      hasModalInfo: !!pointsInfo?.description,
      icon: pointsInfo?.icon?.[0].filename || '',
      modalTitle: pointsInfo?.title,
      modalDescription: pointsInfo?.description,
    },
    annuity: {
      highlightTitle: '',
      highlightDescription: '',
      hasModalInfo: false,
      icon: annuityInfo?.icon?.[0]?.filename || '',
      modalDescription: annuityInfo?.description,
      modalTitle: annuityInfo?.title,
    },
  };

  const [modalData, setModalData] = useState({
    isOpen: false,
    iconUrl: '',
    content: {},
    title: {},
  });

  function getAnnuityText(offer?: MatchMakerDecision) {
    if (!offer) return '';
    const { annuityMonthlyFee, minimumSpend50, minimumSpendFull, monthlyExpenses } = offer;
    const hasHalfSpend = minimumSpend50 !== 0 && monthlyExpenses >= minimumSpend50;
    const hasFullSpend = minimumSpendFull !== 0 && monthlyExpenses >= minimumSpendFull;

    if (annuityMonthlyFee === 0) {
      return 'Anuidade grátis';
    }

    if (hasFullSpend) {
      return `Anuidade grátis gastando ${formatCurrency(minimumSpendFull)}`;
    }

    if (hasHalfSpend) {
      return `Anuidade de ${formatCurrency(annuityMonthlyFee / 2)}/mês gastando ${formatCurrency(minimumSpend50)}`;
    }

    return `Anuidade de ${formatCurrency(annuityMonthlyFee)}/mês`;
  }

  function getAnnuityFree(offer?: MatchMakerDecision) {
    if (!offer) return false;
    const { annuityMonthlyFee, monthlyExpenses, minimumSpendFull } = offer;

    return annuityMonthlyFee === 0 || (minimumSpendFull !== 0 && monthlyExpenses >= minimumSpendFull);
  }

  function shouldRenderSection(type: keyof typeof highlights) {
    const cashback = offer?.matchMaker?.rewardsHighlights.cashback;
    const miles = offer?.matchMaker?.rewardsHighlights.miles;
    const offerCategory = offer?.matchMaker?.category;
    switch (type) {
      case 'discount':
        if (offerCategory === 'cashback') return true;
        if (offerCategory === 'cashback-miles') return !!cashback;
        return false;
      case 'income':
        if (offerCategory === 'cashback') return true;
        if (offerCategory === 'cashback-miles') return !!cashback && !miles;
        return false;
      case 'travel':
        if (offerCategory === 'miles') return true;
        if (offerCategory === 'cashback-miles') return !!miles;
        return false;
      case 'points':
        if (offerCategory === 'miles') return true;
        if (offerCategory === 'cashback-miles') return !cashback && !!miles;
        return false;
      case 'annuity':
        return false;
    }
  }

  function handleModalData(dataType: keyof typeof highlights) {
    if (dataType === 'annuity') {
      return setModalData({
        isOpen: true,
        content: highlights?.annuity?.modalDescription || '',
        title: highlights?.annuity?.modalTitle || '',
        iconUrl: highlights?.annuity?.icon || '',
      });
    }

    if (dataType === 'travel') {
      return setModalData({
        isOpen: true,
        content: highlights?.travel?.modalDescription || '',
        title: highlights?.travel?.modalTitle || '',
        iconUrl: highlights?.travel?.icon || '',
      });
    }

    if (dataType === 'discount') {
      return setModalData({
        isOpen: true,
        content: highlights?.discount?.modalDescription || '',
        title: highlights?.discount?.modalTitle || '',
        iconUrl: highlights?.discount?.icon || '',
      });
    }
  }

  function handleCloseModal() {
    setModalData({
      isOpen: false,
      content: {},
      title: {},
      iconUrl: '',
    });
  }

  return {
    annuityText,
    highlights,
    isAnnuityFree,
    modalData,
    handleModalData,
    handleCloseModal,
    shouldRenderSection,
  };
};
